import * as React from "react";
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowsProp,
  GridRowId,
  GridRowParams,
  GridFilterModel,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { itIT } from "@mui/x-data-grid/locales";
import Box from "@mui/material/Box";

export interface ReadOnlyField {
  field: string;
  headerName: string;
  width?: number | "auto";
  /** Con width "auto" (flex), larghezza minima colonna */
  minWidth?: number;
  type?: "string" | "number" | "date" | "dateTime";
  align?: "left" | "right" | "center";
  headerAlign?: "left" | "right" | "center";
  valueFormatter?: (value: any) => string;
  renderCell?: (params: any) => React.ReactNode;
  /** Colonna fissa a sinistra o a destra (come in edai) */
  sticky?: "left" | "right";
  stickyLeft?: number;
  stickyRight?: number;
}

export interface RowAction {
  label: string;
  icon: React.ReactElement;
  onClick: (id: GridRowId, row: any) => void;
  color?: "inherit" | "primary" | "default";
}

export interface ReadOnlyDataGridProps {
  fields: ReadOnlyField[];
  data: any[];
  actions?: RowAction[];
  height?: string | number;
  getId?: (row: any) => string | number;
  loading?: boolean;
  getRowId?: (row: any) => string | number;
  showFooter?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  initialSortModel?: { field: string; sort: "asc" | "desc" }[];
  onFilterModelChange?: (model: GridFilterModel) => void;
  showToolbar?: boolean;
}

export function ReadOnlyDataGrid({
  fields,
  data,
  actions = [],
  height = 400,
  getId,
  loading = false,
  getRowId,
  showFooter = true,
  initialPageSize = 50,
  pageSizeOptions = [25, 50, 100],
  initialSortModel,
  onFilterModelChange,
  showToolbar = true,
}: ReadOnlyDataGridProps) {
  const getFieldWidthPx = (field: ReadOnlyField) =>
    field.width === "auto" ? 100 : (field.width || 150);

  // Posizioni per colonne sticky left
  const stickyLeftPositions = React.useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulative = 0;
    fields.forEach((f) => {
      if (f.sticky === "left") {
        positions[f.field] = f.stickyLeft ?? cumulative;
        cumulative += getFieldWidthPx(f);
      }
    });
    return positions;
  }, [fields]);

  // Posizioni per colonne sticky right (dall’ultima colonna verso sinistra)
  const stickyRightPositions = React.useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulative = 0;
    for (let i = fields.length - 1; i >= 0; i--) {
      const f = fields[i];
      if (f.sticky === "right") {
        positions[f.field] = f.stickyRight ?? cumulative;
        cumulative += getFieldWidthPx(f);
      }
    }
    return positions;
  }, [fields]);

  const stickyLeftFields = fields.filter((f) => f.sticky === "left");
  const stickyRightFields = fields.filter((f) => f.sticky === "right");
  const hasStickyColumns = stickyLeftFields.length > 0 || stickyRightFields.length > 0;

  // MIT DataGrid: pageSize massimo 100. Oltre quella soglia il footer deve
  // restare visibile, altrimenti le righe successive non sono raggiungibili.
  const MIT_MAX_PAGE_SIZE = 100;
  const safePageSize = Math.min(initialPageSize, MIT_MAX_PAGE_SIZE);
  const paginationVisible = showFooter || data.length > MIT_MAX_PAGE_SIZE;
  const [paginationModel, setPaginationModel] =
    React.useState<GridPaginationModel>({
      page: 0,
      pageSize: safePageSize,
    });

  const wasPaginationVisibleRef = React.useRef(paginationVisible);
  React.useEffect(() => {
    if (!paginationVisible) {
      setPaginationModel({
        page: 0,
        pageSize: Math.max(Math.min(data.length, MIT_MAX_PAGE_SIZE), 1),
      });
    } else if (!wasPaginationVisibleRef.current) {
      setPaginationModel({ page: 0, pageSize: safePageSize });
    }
    wasPaginationVisibleRef.current = paginationVisible;
  }, [paginationVisible, data.length, safePageSize]);

  // Convert data to GridRowsProp format
  const gridRows: GridRowsProp = React.useMemo(() => {
    return data.map((row, index) => {
      const gridRow: any = {
        id: getRowId ? getRowId(row) : getId ? getId(row) : row.id || index,
        ...row,
      };
      // Assicurati che i campi definiti nei fields abbiano la priorità
      fields.forEach((field) => {
        const value = row[field.field];
        gridRow[field.field] = value ?? null;
      });
      return gridRow;
    });
  }, [data, fields, getId, getRowId]);

  // Build columns from fields config
  const columns: GridColDef[] = React.useMemo(() => {
    const fieldColumns: GridColDef[] = fields.map((field) => {
      const hasCustomFormatter = typeof field.valueFormatter === "function";
      const valueFormatter = hasCustomFormatter
        ? (params: any) => {
            if (!params) return "";
            return field.valueFormatter?.(params?.value ?? params ?? "") ?? "";
          }
        : field.type === "dateTime" || field.type === "date"
          ? (value: any) =>
              value ? new Date(value).toLocaleDateString("it-IT") : ""
          : undefined;

      const column: GridColDef = {
        field: field.field,
        headerName: field.headerName,
        ...(field.width === "auto"
          ? { flex: 1, minWidth: field.minWidth ?? 100 }
          : { width: field.width || 150 }),
        type: field.type,
        align: field.align,
        headerAlign: field.headerAlign,
        valueFormatter,
      };

      // Se c'è un renderCell personalizzato, usalo
      if (field.renderCell) {
        column.renderCell = field.renderCell;
      }

      if (field.sticky === "left" || field.sticky === "right") {
        const stickyClassName = `sticky-column-${field.field}`;
        column.headerClassName = stickyClassName;
        column.cellClassName = stickyClassName;
      }

      return column;
    });

    // Add actions column if actions are provided
    if (actions.length > 0) {
      const actionsColumn: GridColDef = {
        field: "actions",
        type: "actions",
        headerName: "",
        width: actions.length > 1 ? 120 : 100,
        align: "right",
        headerAlign: "right",
        getActions: (params: GridRowParams) => {
          return actions.map((action) => (
            <GridActionsCellItem
              key={action.label}
              icon={action.icon}
              label={action.label}
              onClick={() => action.onClick(params.id, params.row)}
              color="primary"
            />
          ));
        },
      };
      return [...fieldColumns, actionsColumn];
    }

    return fieldColumns;
  }, [fields, actions]);

  return (
    <Box
      sx={{
        height,
        width: "100%",
      }}
    >
      <DataGrid
        sx={{
          borderRadius: 2,
          "& .MuiDataGrid-overlayWrapper": { backgroundColor: "#fff" },
          "& .MuiDataGrid-overlay": { backgroundColor: "#fff" },
          ...(hasStickyColumns &&
            fields.reduce(
              (acc, field) => {
                if (field.sticky === "left") {
                  const stickyClassName = `sticky-column-${field.field}`;
                  const leftPx = stickyLeftPositions[field.field] ?? 0;
                  acc[`& .MuiDataGrid-cell.${stickyClassName}`] = {
                    position: "sticky",
                    left: `${leftPx}px`,
                    zIndex: 125,
                    backgroundColor: "background.paper",
                  };
                  acc[`& .MuiDataGrid-columnHeader.${stickyClassName}`] = {
                    position: "sticky",
                    left: `${leftPx}px`,
                    zIndex: 126,
                    backgroundColor: "background.paper",
                  };
                }
                if (field.sticky === "right") {
                  const stickyClassName = `sticky-column-${field.field}`;
                  const rightPx = stickyRightPositions[field.field] ?? 0;
                  acc[`& .MuiDataGrid-cell.${stickyClassName}`] = {
                    position: "sticky",
                    right: `${rightPx}px`,
                    zIndex: 125,
                    backgroundColor: "background.paper",
                  };
                  acc[`& .MuiDataGrid-columnHeader.${stickyClassName}`] = {
                    position: "sticky",
                    right: `${rightPx}px`,
                    zIndex: 126,
                    backgroundColor: "background.paper",
                  };
                }
                return acc;
              },
              {} as Record<string, object>
            )),
        }}
        pageSizeOptions={
          paginationVisible
            ? pageSizeOptions.filter((n) => n <= MIT_MAX_PAGE_SIZE)
            : [paginationModel.pageSize]
        }
        paginationModel={paginationModel}
        onPaginationModelChange={(model) =>
          setPaginationModel({
            ...model,
            pageSize: Math.min(model.pageSize, MIT_MAX_PAGE_SIZE),
          })
        }
        initialState={{
          filter: {
            filterModel: {
              items: [],
            },
          },
          ...(initialSortModel?.length
            ? { sorting: { sortModel: initialSortModel } }
            : {}),
        }}
        showToolbar={showToolbar}
        onFilterModelChange={onFilterModelChange}
        rows={gridRows}
        columns={columns}
        loading={loading}
        hideFooter={!paginationVisible}
        hideFooterPagination={!paginationVisible}
        hideFooterSelectedRowCount={!paginationVisible}
        disableRowSelectionOnClick
        checkboxSelection={false}
        disableColumnSelector={true}
        rowBufferPx={100}
        columnBufferPx={hasStickyColumns ? 10000 : undefined}
        getRowId={getRowId}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
      />
    </Box>
  );
}
